// apps/client/src/assets/loader.ts — ports of look-demo assets.ts prepare (as prepareToy), centred and batchStatic, plus the kit loader.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** Every kit material gets the same soft toy finish: matte, no metal, casting and catching shadows. Mutates and returns `root`. */
export function prepareToy(root: THREE.Object3D): THREE.Object3D {
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    m.castShadow = true;
    m.receiveShadow = true;
    for (const mat of [m.material].flat() as THREE.MeshStandardMaterial[]) {
      mat.metalness = 0;
      mat.roughness = Math.max(0.78, mat.roughness ?? 1);
    }
  });
  return root;
}

/** Recentres a model so its footprint sits on the origin and its base on y = 0. */
export function centred(o: THREE.Object3D): { wrap: THREE.Group; size: THREE.Vector3 } {
  o.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(o);
  const c = box.getCenter(new THREE.Vector3());
  o.position.sub(new THREE.Vector3(c.x, box.min.y, c.z));
  const wrap = new THREE.Group();
  wrap.add(o);
  return { wrap, size: box.getSize(new THREE.Vector3()) };
}

/** Texture slots a merged material may not carry beyond `map` (a mesh using any of them keeps its own material). */
const OTHER_MAPS = ['lightMap', 'aoMap', 'emissiveMap', 'bumpMap', 'normalMap', 'displacementMap', 'roughnessMap', 'metalnessMap', 'alphaMap', 'envMap'] as const;
/** The merge key of an opaque, unlit-by-itself MeshStandardMaterial whose colour can move into the vertices, or null. Glowing ones
 *  (window panes, whose emissive the clock drives), see-through ones and anything with extra maps keep their own material. */
function colourlessKey(mat: THREE.Material): string | null {
  const m = mat as THREE.MeshStandardMaterial;
  if (!m.isMeshStandardMaterial || (m as THREE.MeshPhysicalMaterial).isMeshPhysicalMaterial || m.transparent || m.vertexColors || m.alphaTest > 0) return null;
  if (m.emissive.getHex() !== 0 || OTHER_MAPS.some((k) => (m as unknown as Record<string, unknown>)[k])) return null;
  return `rgb:${m.map?.uuid ?? ''}:${m.roughness}:${m.metalness}:${m.side}:${m.flatShading}:${m.fog}`;
}
/** One white vertex-coloured copy per key, kept across rebatches (a new material per rebatch would compile its shader again). */
const colourless = new Map<string, THREE.MeshStandardMaterial>();

/** The object a static mesh belongs to, for the occlusion fade (engine/shader/fade.ts): a key (its view) and its fade cylinder
 *  (x, z, −radius, top). */
export interface StaticOwner { key: object; obj: readonly [number, number, number, number] }
/** Merges every static mesh under `root` into one mesh per material, so a town of props costs a few draw calls. Materials that differ
 *  only in colour (friend dyes, tinted kit pieces) merge further into one mesh with the colour in its vertices. With `ownerOf`, every
 *  vertex carries its object's `aObj` (zeros for none) and each merged mesh's `userData.ranges` maps an owner's key to the vertices
 *  [start, end) it holds there (one run: a view's meshes are merged in order), so a faded house's ghost draws just its own. */
export function batchStatic(root: THREE.Object3D, ownerOf?: (mesh: THREE.Mesh) => StaticOwner | null): THREE.Group {
  root.updateMatrixWorld(true);
  const groups = new Map<string, { material: THREE.Material; geos: THREE.BufferGeometry[]; owners: (object | null)[]; coloured: boolean }>();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || Array.isArray(m.material)) return;
    const g = m.geometry.clone().applyMatrix4(m.matrixWorld);
    for (const name of Object.keys(g.attributes)) if (name !== 'position' && name !== 'normal' && name !== 'uv') g.deleteAttribute(name);
    if (!g.attributes.uv) g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(g.attributes.position!.count * 2), 2));
    const geo = g.index ? g.toNonIndexed() : g;
    const ck = colourlessKey(m.material);
    if (ck) {
      const c = (m.material as THREE.MeshStandardMaterial).color, n = geo.attributes.position!.count, col = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) { col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
      geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    }
    const owner = ownerOf?.(m) ?? null;
    if (ownerOf) {
      const n = geo.attributes.position!.count, obj = new Float32Array(n * 4);
      if (owner) for (let i = 0; i < n; i++) obj.set(owner.obj, i * 4);
      geo.setAttribute('aObj', new THREE.BufferAttribute(obj, 4));
    }
    const key = ck ?? m.material.uuid;
    if (!groups.has(key)) groups.set(key, { material: m.material, geos: [], owners: [], coloured: !!ck });
    groups.get(key)!.geos.push(geo);
    groups.get(key)!.owners.push(owner?.key ?? null);
  });
  const out = new THREE.Group();
  for (const [key, { material, geos, owners, coloured }] of groups) {
    const ranges = new Map<object, [number, number]>();
    let at = 0;
    geos.forEach((g, i) => {
      const n = g.attributes.position!.count, k = owners[i];
      if (k) { const r = ranges.get(k); ranges.set(k, r ? [Math.min(r[0], at), Math.max(r[1], at + n)] : [at, at + n]); }
      at += n;
    });
    const merged = mergeGeometries(geos, false);
    for (const g of geos) g.dispose();
    if (!merged) continue;
    let mat = material;
    if (coloured) {
      let shared = colourless.get(key);
      if (!shared) {
        const src = material as THREE.MeshStandardMaterial;
        shared = new THREE.MeshStandardMaterial({ map: src.map, roughness: src.roughness, metalness: src.metalness, side: src.side,
          flatShading: src.flatShading, fog: src.fog, vertexColors: true });
        colourless.set(key, shared);
      }
      mat = shared;
    }
    const mesh = new THREE.Mesh(merged, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    if (ownerOf) mesh.userData.ranges = ranges;
    out.add(mesh);
  }
  return out;
}

type KitGltf = { scene: THREE.Object3D; animations: THREE.AnimationClip[] };
let loader: GLTFLoader | null = null;
const cache = new Map<string, Promise<KitGltf>>();
/** A built kit GLB (meshopt-compressed by tools/assets), loaded once per URL. */
export function loadKitGltf(url: string): Promise<KitGltf> {
  let p = cache.get(url);
  if (!p) {
    loader ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    p = loader.loadAsync(url).then((g) => ({ scene: g.scene, animations: g.animations }));
    p.catch(() => cache.delete(url));                  // a failed fetch may be retried
    cache.set(url, p);
  }
  return p;
}
